import React, { useState } from "react";
import { useAuth } from "../auth";
import ProfileEdit from "./ProfileEdit";
import PasswordField from "./PasswordField";
import { friendlyError } from "../utils/errors";

function signInProviders(user) {
  const list = user?.app_metadata?.providers || [];
  if (list.length) return list;
  return user?.app_metadata?.provider ? [user.app_metadata.provider] : [];
}

/** New-password form: used from "Seguridad" and after a reset-password email. */
export function ChangePasswordForm({ onDone, submitLabel = "Cambiar contraseña" }) {
  const { updatePassword } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setDone(false);
    if (password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (password !== confirm) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    setBusy(true);
    try {
      await updatePassword(password);
      setPassword("");
      setConfirm("");
      setDone(true);
      onDone?.();
    } catch (err) {
      console.error(err);
      const msg = err?.message || "";
      setError(
        /reauth|nonce|recent/i.test(msg)
          ? "Por seguridad, cierra sesión, vuelve a entrar e inténtalo de nuevo."
          : /different from the old|same.*password/i.test(msg)
            ? "La contraseña nueva debe ser distinta a la actual."
            : friendlyError(err, "No se pudo cambiar la contraseña. Inténtalo de nuevo.")
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="reg-form acct-form" onSubmit={onSubmit} noValidate>
      <PasswordField
        label="Contraseña nueva"
        value={password}
        onChange={setPassword}
        autoComplete="new-password"
        disabled={busy}
        hint="Mínimo 6 caracteres."
      />
      <PasswordField
        label="Confirmar contraseña nueva"
        value={confirm}
        onChange={setConfirm}
        autoComplete="new-password"
        disabled={busy}
      />
      <div aria-live="polite">
        {error && (
          <div className="reg-status error" role="alert">
            {error}
          </div>
        )}
        {done && (
          <div className="reg-success" role="status">
            Contraseña actualizada.
          </div>
        )}
      </div>
      <button type="submit" className="reg-submit acct-save" disabled={busy}>
        {busy ? "Guardando…" : submitLabel}
      </button>
    </form>
  );
}

/** "Cuenta" tab: profile, notifications, security and sign-out. */
export default function AccountPanel() {
  const { user, signOut } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const providers = signInProviders(user);
  const usesGoogle = providers.includes("google");
  const usesEmail = providers.includes("email");

  const onSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
    } catch (err) {
      console.error(err);
      setSigningOut(false);
    }
  };

  return (
    <div className="acct-panel">
      <section className="acct-section" aria-labelledby="acct-profile-title">
        <header className="acct-section-head">
          <h2 className="acct-section-title" id="acct-profile-title">
            Perfil y avisos
          </h2>
          <p className="acct-section-desc">
            Tus datos de contacto y cómo quieres recibir avisos de la liga.
          </p>
        </header>
        <ProfileEdit />
      </section>

      <section className="acct-section" aria-labelledby="acct-security-title">
        <header className="acct-section-head">
          <h2 className="acct-section-title" id="acct-security-title">
            Acceso
          </h2>
        </header>

        <dl className="acct-facts">
          <div className="acct-fact">
            <dt>Correo</dt>
            <dd>{user?.email}</dd>
          </div>
          <div className="acct-fact">
            <dt>Inicias sesión con</dt>
            <dd>
              {[usesGoogle && "Google", usesEmail && "Correo y contraseña"]
                .filter(Boolean)
                .join(" y ") || "Correo"}
            </dd>
          </div>
        </dl>

        {usesEmail ? (
          showPassword ? (
            <ChangePasswordForm
              onDone={() => {
                setShowPassword(false);
                setPasswordChanged(true);
              }}
            />
          ) : (
            <>
              {passwordChanged && (
                <div className="reg-success" role="status">
                  Contraseña actualizada.
                </div>
              )}
              <button
                type="button"
                className="reg-btn-secondary acct-inline-btn"
                onClick={() => {
                  setPasswordChanged(false);
                  setShowPassword(true);
                }}
              >
                Cambiar contraseña
              </button>
            </>
          )
        ) : (
          <p className="reg-hint">
            Tu contraseña la maneja Google. El correo de la cuenta no se puede
            cambiar aquí.
          </p>
        )}
      </section>

      <section className="acct-section acct-section--quiet" aria-label="Cerrar sesión">
        <button
          type="button"
          className="acct-signout"
          onClick={onSignOut}
          disabled={signingOut}
        >
          {signingOut ? "Cerrando sesión…" : "Cerrar sesión"}
        </button>
      </section>
    </div>
  );
}
